import React, { useState, useEffect } from 'react';
import {
  Users,
  Briefcase,
  Check,
  PlusCircle,
  Save,
  AlertCircle,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const permissionsCategories = {
  'POS Core': ['POS Core: View', 'POS Core: Edit', 'POS Core: Update'],
  'Inventory Core': ['Inventory Core: View', 'Inventory Core: Edit', 'Inventory Core: Update'],
  'Account Core': ['Account Core: View', 'Account Core: Edit', 'Account Core: Update'],
  'Other': ['View Sales', 'Manage Inventory', 'Edit Users'],
};

const allPermissions = Object.values(permissionsCategories).flat();

function BulkRoleAssignment() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedRoleForAssignment, setSelectedRoleForAssignment] = useState('');
  const [expandedRoleDetails, setExpandedRoleDetails] = useState(null);

  // States for new role creation
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState({});
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [newRoleStatus, setNewRoleStatus] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';
        // Fetch users
        const usersRes = await axios.get(`${apiBase}/admin/users?db=alwaysdata`);
        setUsers(usersRes.data.users);

        // Fetch roles
        const rolesRes = await axios.get(`${apiBase}/roles?db=alwaysdata`);
        const rolesObj = {};
        rolesRes.data.roles.forEach(r => {
          rolesObj[r.name] = r.permissions;
        });
        setRoles(rolesObj);

      } catch (err) {
        console.error(err);
        toast.error('Failed to load users or roles');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle user checkbox selection
  const handleUserSelect = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUserIds(users.map(user => user.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  // Bulk role assignment
  const handleBulkRoleAssignment = async () => {
    if (!selectedRoleForAssignment || selectedUserIds.length === 0) {
      setStatusMessage({ type: 'error', text: 'Select role and users.' });
      return;
    }

    try {
      setLoading(true);
      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';
      await axios.post(`${apiBase}/admin/assign-role?db=alwaysdata`, {
        userIds: selectedUserIds,
        roleName: selectedRoleForAssignment
      });

      // Update frontend users list
      const updatedUsers = users.map(u =>
        selectedUserIds.includes(u.id)
          ? { ...u, role: selectedRoleForAssignment }
          : u
      );
      setUsers(updatedUsers);
      setSelectedUserIds([]);
      setSelectedRoleForAssignment('');
      setStatusMessage({ type: 'success', text: 'Role assigned successfully.' });
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (err) {
      console.error(err);
      setStatusMessage({ type: 'error', text: 'Failed to assign role.' });
    } finally {
      setLoading(false);
    }
  };

  // New role creation
  const handleNewRolePermissionChange = (perm, value) => {
    setNewRolePermissions(prev => ({ ...prev, [perm]: value }));
  };

  const handleCreateNewRole = async () => {
    if (!newRoleName.trim()) {
      setNewRoleStatus({ type: 'error', text: 'Enter role name.' });
      return;
    }

    try {
      setLoading(true);
      const permissionsArray = Object.keys(newRolePermissions).filter(k => newRolePermissions[k]);

      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';
      await axios.post(`${apiBase}/roles/create?db=alwaysdata`, {
        name: newRoleName,
        permissions: permissionsArray
      });

      // Refresh roles
      const rolesRes = await axios.get('http://localhost:4000/api/roles?db=alwaysdata');
      const rolesObj = {};
      rolesRes.data.roles.forEach(r => { rolesObj[r.name] = r.permissions; });
      setRoles(rolesObj);

      setShowNewRoleForm(false);
      setNewRoleName('');
      setNewRolePermissions({});
      setNewRoleStatus(null);
      setStatusMessage({ type: 'success', text: `Role '${newRoleName}' created successfully.` });
      setTimeout(() => setStatusMessage(null), 3000);

    } catch (err) {
      console.error(err);
      setNewRoleStatus({ type: 'error', text: 'Failed to create role.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen p-4 sm:p-8 font-sans">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <h1 className="text-4xl font-extrabold text-slate-900 flex items-center">
            <Briefcase className="w-10 h-10 mr-4 text-sky-500" />
            Role and User Management
          </h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage user roles and permissions dynamically.
          </p>
        </header>

        {statusMessage && (
          <div
            className={`p-4 rounded-xl mb-6 flex items-center gap-3 transition-opacity duration-300 ${
              statusMessage.type === 'success'
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {statusMessage.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-semibold">{statusMessage.text}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Assign Roles */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-4">Assign Roles to Users</h2>
              <div className="flex items-center gap-4 mb-4">
                <select
                  value={selectedRoleForAssignment}
                  onChange={(e) => setSelectedRoleForAssignment(e.target.value)}
                  className="flex-grow px-4 py-3 border rounded-lg focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">Select a role</option>
                  {Object.keys(roles).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <button
                  onClick={handleBulkRoleAssignment}
                  disabled={!selectedRoleForAssignment || selectedUserIds.length === 0}
                  className="px-6 py-3 bg-sky-600 text-white rounded-full font-semibold hover:bg-sky-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-5 h-5 inline-block mr-1"/> Assign Role
                </button>
              </div>

              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-100">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedUserIds.length === users.length && users.length > 0}
                          onChange={handleSelectAll}
                          className="h-5 w-5 text-sky-600 rounded border-slate-300 focus:ring-sky-500"
                        />
                      </th>
                      <th>Name</th>
                      <th>Current Role</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.id)}
                            onChange={() => handleUserSelect(u.id)}
                            className="h-5 w-5 text-sky-600 rounded border-slate-300 focus:ring-sky-500"
                          />
                        </td>
                        <td>{u.name}</td>
                        <td>{u.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right Panel: New Role Creation */}
            <div className="lg:col-span-1 bg-white rounded-xl shadow-lg p-6 border border-slate-200 h-fit">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Create New Role</h2>
                <button
                  onClick={() => setShowNewRoleForm(!showNewRoleForm)}
                  className="p-2 rounded-full bg-sky-100 text-sky-600 hover:bg-sky-200 transition-colors"
                >
                  <PlusCircle className="w-5 h-5"/>
                </button>
              </div>

              {showNewRoleForm && (
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  {newRoleStatus && (
                    <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                      newRoleStatus.type === 'error' ? 'bg-red-100 text-red-700' : ''
                    }`}>
                      <AlertCircle className="w-4 h-4" /> {newRoleStatus.text}
                    </div>
                  )}
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="Role name"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500"
                  />
                  <h3 className="font-semibold">Permissions</h3>
                  {Object.keys(permissionsCategories).map(group => (
                    <div key={group}>
                      <h4 className="font-medium">{group}</h4>
                      <div className="space-y-1 pl-4">
                        {permissionsCategories[group].map(perm => (
                          <label key={perm} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newRolePermissions[perm] || false}
                              onChange={(e) => handleNewRolePermissionChange(perm, e.target.checked)}
                              className="h-4 w-4 text-sky-600"
                            />
                            {perm}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={handleCreateNewRole}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors"
                  >
                    <PlusCircle className="w-5 h-5 inline-block mr-1"/> Create Role
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BulkRoleAssignment;
